import React, { useEffect, useMemo, useState } from "react";
import {
  Upload,
  Download,
  Search,
  ChevronDown,
  CircleAlert,
  ArrowDownToLine,
  Calendar,
  X,
} from "lucide-react";
import "./BeatMapManagement.scss";
import {
  BeatMapLogsAction,
  BeatMapLogsDetailsAction,
  BeatMapPostAction,
  dowloadMaplistAction,
} from "../../actions/beatMapAction";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { notify } from '../../services/notification';
import Header from "../../component/loaderComponent/Header/Header";
import { constant } from "../../constants/constant";

const ValidationResultsModal = ({
  open,
  title = "Validation Results",
  totalErrors = 0,
  items = [],
  onClose,
  onDownloadErrorReport,
  onTryAgain,
}) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && open && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="vr-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="vr-title"
    >
      <div className="vr-modal">
        <button className="vr-close" aria-label="Close" onClick={onClose}>
          <X size={18} />
        </button>
        <div className="vr-header">
          <h3 id="vr-title">{title}</h3>
        </div>
        <div className="vr-body">
          <div className="vr-banner">
            <span className="vr-dot" />
            <div>
              <div className="vr-banner-title">Upload Failed</div>
              <div className="vr-banner-sub">
                {totalErrors} errors found in your file
              </div>
            </div>
          </div>

          <div className="vr-errors">
            {items.map((e) => (
              <div className="vr-error" key={e.id}>
                <CircleAlert size={16} />
                <div>
                  {/* <div className="vr-error-title">{e.title}</div> */}
                  <div className="vr-error-msg">{e.message}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="vr-footer">
          {/* <button className="vr-btn vr-btn--secondary" onClick={onDownloadErrorReport}>
            Download Error Report
          </button> */}
          <button className="vr-btn vr-btn--primary" onClick={onTryAgain}>
            Try Again
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Lightweight skeleton row renderer that preserves table layout.
 * Add minimal styles to BeatMapManagement.scss (provided below).
 */
const SkeletonRows = ({ rows = 5 }) => {
  return Array.from({ length: rows }).map((_, idx) => (
    <tr key={`sk-${idx}`} className="thead-td">
      <td>
        <div className="sk sk-line w-64" />
      </td>
      <td>
        <div className="sk sk-line w-72" />
      </td>
      <td>
        <div className="sk sk-badge w-24" />
      </td>
      <td>
        <div className="sk sk-line w-16" />
      </td>
      <td>
        <div className="sk sk-actions" />
      </td>
    </tr>
  ));
};

const BeatMapManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // filters
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [timeFilter, setTimeFilter] = useState("Last 90 days");

  // data + pagination
  const pageSize = constant?.paginationCount || 10;
  const [uploadHistory, setUploadHistory] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((totalCount || 0) / pageSize)),
    [totalCount, pageSize]
  );

  // ui + misc
  const [dragActive, setDragActive] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [selectedLogId, setSelectedLogId] = useState(null);

  // helpers
  const getStatusClass = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case "success":
      case "completed":
        return "status success";
      case "failed":
      case "error":
        return "status failed";
      case "processing":
      case "pending":
        return "status processing";
      default:
        return "status default";
    }
  };

  const getDisplayStatus = (status) => {
    const statusMap = {
      success: "Success",
      completed: "Success",
      failed: "Failed",
      error: "Failed",
      processing: "Processing",
      pending: "Processing",
    };
    return statusMap[status?.toLowerCase()] || status;
  };

  const pageWindow = useMemo(() => {
    const pages = [];
    const maxButtons = 7;
    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }

    const showLeftEllipsis = currentPage > 4;
    const showRightEllipsis = currentPage < totalPages - 3;

    pages.push(1);
    if (showLeftEllipsis) pages.push("left-ellipsis");

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let i = start; i <= end; i++) pages.push(i);

    if (showRightEllipsis) pages.push("right-ellipsis");
    pages.push(totalPages);

    return pages;
  }, [currentPage, totalPages]);

  // data fetch
  const fetchUploadHistory = async () => {
    try {
      setLoading(true);
      let query = "";

      if (searchTerm) {
        query += `&search=${encodeURIComponent(searchTerm)}`;
      }

      if (statusFilter !== "All Status") {
        const statusMap = {
          Success: "success",
          Failed: "failed",
          Processing: "processing",
        };
        query += `&status=${statusMap[statusFilter]}`;
      }

      const res = await dispatch(
        BeatMapLogsAction(currentPage, pageSize, query)
      );

      if (res?.status === 200) {
        const historyData = res?.data?.results || [];
        setUploadHistory(Array.isArray(historyData) ? historyData : []);
        const count = res?.data?.count || 0;
        setTotalCount(count);

        // clamp current page if it went out of range
        const lastPage = Math.max(1, Math.ceil(count / pageSize));
        if (currentPage > lastPage) setCurrentPage(lastPage);
      }
    } catch {
      notify.error("Failed to load upload history");
    } finally {
      setLoading(false);
    }
  };

  const fetchValidationErrors = async (logId) => {
    try {
      const response = await dispatch(BeatMapLogsDetailsAction(logId));
      if (response?.status === 200) {
        const errorData = response.data?.data || [];
        setValidationErrors(errorData);
        return errorData;
      }
    } catch (error) {
      notify.error("Failed to load validation details");
    }
    return [];
  };

  // upload
  const handleFileUpload = async (file) => {
    if (!file) return;

    setUploadLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await dispatch(BeatMapPostAction(formData));

      if (response?.status === 200 || response?.status === 201) {
        notify.success(response?.data?.message);
        setSelectedFile(null);
        setCurrentPage(1);
        await fetchUploadHistory();
      } else {
        if (response?.data?.errors) {
          setSelectedFile(null);
          const errorMessages = response.data.errors;
          const errorItems = Object.keys(errorMessages).map((key) => ({
            id: key,
            title: `Validation Error: ${key}`,
            message: Array.isArray(errorMessages[key])
              ? errorMessages[key][0]
              : errorMessages[key],
          }));
          setValidationErrors(errorItems);
          setShowValidation(true);
        } else {
          notify.error("Upload failed");
        }
      }
    } catch {
      notify.error("Upload failed");
    } finally {
      setUploadLoading(false);
    }
  };

  // downloads
  const handleDownloadTemplate = () => {
    notify.info("Downloading template...");
    const link = document.createElement("a");
    link.href = "/file/beat_map_template.xlsx";
    link.download = "beat_map_template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // const handleDownloadErrorReport = async (logId) => {
  //   try {
  //     const fileBlob = await dispatch(dowloadMaplistAction(logId));
  //     if (fileBlob) {
  //       const blobUrl = URL.createObjectURL(fileBlob);
  //       const link = document.createElement("a");
  //       link.href = blobUrl;
  //       link.download = `error-report-${logId}.xlsx`;
  //       document.body.appendChild(link);
  //       link.click();
  //       document.body.removeChild(link);
  //       URL.revokeObjectURL(blobUrl);
  //     }
  //   } catch (error) {
  //     notify.error("Failed to download error report");
  //   }
  // };

  // validation modal
 
  const handleDownloadErrorReport = async (logId, originalFilename = null) => {
    try {
      const response = await dispatch(dowloadMaplistAction(logId));
 
      if (response && response.blob) {
        // Try to get filename from multiple sources in order of preference:
        // 1. Original filename passed from the table row
        // 2. Content-Disposition header (if CORS allows)
        // 3. Fallback to generic name
 
        let filename = `error-report-${logId}.xlsx`; // fallback filename
 
        // First try: use the original filename from upload history
        if (originalFilename) {
          filename = originalFilename;
          console.log("Using filename from upload history:", filename);
        } else {
          // Second try: Content-Disposition header
          const contentDisposition = response.responseHeaders?.get("content-disposition");
 
          if (contentDisposition) {
            // Try multiple patterns to extract filename
            let filenameMatch = contentDisposition.match(/filename\s*=\s*"([^"]+)"/);
 
            if (!filenameMatch) {
              filenameMatch = contentDisposition.match(/filename\s*=\s*([^;]+)/);
            }
 
            if (filenameMatch && filenameMatch[1]) {
              filename = filenameMatch[1].trim().replace(/['"]/g, '');
              console.log("Extracted filename from header:", filename);
            }
          }
        }
 
        const blobUrl = URL.createObjectURL(response.blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
 
        console.log("Download initiated with filename:", filename);
      }
    } catch (error) {
      console.error("Download error:", error);
      notify.error("Failed to download error report");
    }
  };
  
  const handleViewValidation = async (logId) => {
    setSelectedLogId(logId);
    const errors = await fetchValidationErrors(logId);

    const modalErrors = errors.map((error) => ({
      id: error.id,
      title: `Error ${error.id}`,
      message: error.error_message,
    }));

    setValidationErrors(modalErrors);
    setShowValidation(true);
  };

  // Delete log
  const handleDeleteLog = async (logId) => {
    if (window.confirm("Are you sure you want to delete this upload record?")) {
      try {
        notify.info("Delete functionality to be implemented");
        fetchUploadHistory();
      } catch (error) {
        notify.error("Failed to delete record");
      }
    }
  };

  // Drag and drop handlers
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (
        file.type.includes("excel") ||
        file.type.includes("spreadsheet") ||
        file.name.endsWith(".xlsx") ||
        file.name.endsWith(".xls")
      ) {
        setSelectedFile(file);
        handleFileUpload(file);
      } else {
        notify.error(
          "Please upload only Excel files (.xlsx, .xls)"
        );
      }
    }
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      handleFileUpload(file);
    }
  };

  // effects
  useEffect(() => {
    fetchUploadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, statusFilter, timeFilter]);

  const filteredHistory = useMemo(() => {
    if (!Array.isArray(uploadHistory)) return [];
    return uploadHistory.filter((item) => {
      const matchesSearch =
        item.excel_file_name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        item.created_at?.includes(searchTerm);
      const matchesStatus =
        statusFilter === "All Status" ||
        getDisplayStatus(item.status) === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [uploadHistory, searchTerm, statusFilter]);

  const showingFrom = totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const showingTo = Math.min(currentPage * pageSize, totalCount);

  return (
    <div className="beatmap-container">
      {/* Header */}
      <div className="header">
        <Header title="Beat Map Management" />
        <div className="sub-header">
          Upload and manage customer mappings to DSRs and create their
          weekly/monthly route plans
        </div>
      </div>

      {/* Main Content */}
      <div className="content">
        {/* Upload Section */}
        <div className="card">
          <div className="card-header">
            <h2>Upload Beat Map</h2>
          </div>

          {/* Quick Guide Section */}
          <div className="quick-guide-section">
            <div className="quick-guide-header">
              <CircleAlert size={18} className="guide-icon" />
              <h3>Beat Map Upload – Quick Guide</h3>
            </div>
            <ul className="quick-guide-list">
              <li>
                <strong>POMS Customer Code:</strong> Enter the full code exactly as it appears in your POMS system (e.g., 30105-1111).
              </li>
              <li>
                <strong>POMS Employee Code:</strong> Enter the full code exactly as it appears in your POMS system (e.g., EMP-111).
              </li>
              <li>
                <strong>Days Format:</strong> In the Excel file, use 1–7 for 'Days':
                <span className="days-format"> 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat, 7=Sun</span>
              </li>
              <li>
                <strong>Validity:</strong> A beat map is valid only for the current month.
              </li>
              <li>
                <strong>Monthly Upload:</strong> You must upload a new beat map every month, preferably on the first day of the month.
              </li>
              <li>
                <strong>Re-upload:</strong> If you re-upload the beat map in the middle of the month, the existing beat plan will get updated.
              </li>
              <li>
                <strong>Effective Date:</strong> Any new beat map upload or changes will become effective from the next day onwards.
              </li>
            </ul>
          </div>

          <div className="card-body">
            <div className="button-group">
              <label htmlFor="file-upload" className="btn primary">
                <Upload size={16} />
                {uploadLoading ? "Uploading..." : "Upload Beat Map"}
              </label>
              <input
                id="file-upload"
                type="file"
                accept=".xlsx,.xls"
                style={{ display: "none" }}
                onChange={handleFileInput}
                disabled={uploadLoading}
              />
              <button
                className="btn secondary"
                onClick={handleDownloadTemplate}
                disabled={uploadLoading}
              >
                <Download size={16} /> Download Template
              </button>
            </div>

            {/* Drop Zone */}
            <div
              className={`drop-zone ${dragActive ? "active" : ""} ${
                uploadLoading ? "disabled" : ""
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <div className="drop-zone-content">
                {uploadLoading ? (
                  <div className="uploading">
                    <div className="spinner"></div>
                    <p>Uploading {selectedFile?.name}...</p>
                  </div>
                ) : (
                  <>
                    <div className="icon">
                      <Upload size={24} className="upload-icon" />
                    </div>
                    <p className="title">
                      Drop your Excel file here or click to browse
                    </p>
                    <p className="subtitle">
                      Supported formats: .xlsx, .xls (Max size: 10MB)
                    </p>
                    <p className="note">
                      Required fields: POMS Customer Code,	POMS Employee Code,	Days
                    </p>
                    {selectedFile && (
                      <p className="selected-file">
                        Selected: {selectedFile.name}
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Upload History Section */}
        <div className="card">
          <div className="table-header">
            <h2>Upload History</h2>

            {/* Keep filters area to preserve UI spacing; controls can be enabled later */}
            <div className="filters-container">
              <div className="filters-header">
                <div className="filters-left"></div>
                <div className="filters-right">
                  {/*
                  <div className="filter-group">
                    <div className="search-box">
                      <Search size={16} className="search-icon" />
                      <input
                        type="text"
                        placeholder="Search files..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="select-box">
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                      <option value="All Status">All Status</option>
                      <option value="Success">Success</option>
                      <option value="Failed">Failed</option>
                      <option value="Processing">Processing</option>
                    </select>
                    <ChevronDown className="chevron" size={16} />
                  </div>
                  <div className="select-box">
                    <select value={timeFilter} onChange={(e) => setTimeFilter(e.target.value)}>
                      <option value="Last 90 days">Last 90 days</option>
                      <option value="Last 30 days">Last 30 days</option>
                      <option value="Last 7 days">Last 7 days</option>
                    </select>
                    <Calendar className="chevron" size={16} />
                  </div>
                  */}
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="table-container">
            <table>
              <thead className="th-headings">
                <tr>
                  <th>Upload Date/Time</th>
                  <th>File Name</th>
                  <th>Status</th>
                  <th>Total Records</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <SkeletonRows rows={pageSize} />
                ) : filteredHistory.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="no-data">
                      No upload history found
                    </td>
                  </tr>
                ) : (
                  filteredHistory.map((item) => (
                    <tr key={item.id} className={`thead-td ${item.status}`}>
                      <td>
                        <div className="date-time">
                          <span>
                            {item.created_at
                              ? new Date(item.created_at).toLocaleString()
                              : "N/A"}
                          </span>
                        </div>
                      </td>
                      <td className="file-link">
                        {item.excel_file_name || "Unknown file"}
                      </td>
                      <td>
                        <span className={getStatusClass(item.status)}>
                          <div className="dot"></div>
                          {getDisplayStatus(item.status)}
                        </span>
                      </td>
                      <td>{item.total_rows?.toLocaleString() || "0"}</td>
                      <td>
                        <div className="actions">
                          <button
                            className={`icon-btn download ${
                              item.status?.toLowerCase() === "pending" ||
                              item.status?.toLowerCase() === "processing"
                                ? "disabled"
                                : ""
                            }`}
                            onClick={() =>
                              item.status?.toLowerCase() !== "pending" &&
                              item.status?.toLowerCase() !== "processing" &&
                              handleDownloadErrorReport(item.id,item.excel_file_name)
                            }
                            title={
                              item.status?.toLowerCase() === "pending" ||
                              item.status?.toLowerCase() === "processing"
                                ? "Processing..."
                                : "Download Error Report"
                            }
                            disabled={
                              item.status?.toLowerCase() === "pending" ||
                              item.status?.toLowerCase() === "processing"
                            }
                          >
                            <ArrowDownToLine size={16} />
                            {(item.status?.toLowerCase() === "pending" ||
                              item.status?.toLowerCase() === "processing") && (
                              <span className="processing-indicator"></span>
                            )}
                          </button>
                          {(item.status?.toLowerCase() === "failed" ||
                            item.status?.toLowerCase() === "error") && (
                            <button
                              className="icon-btn view"
                              onClick={() => handleViewValidation(item.id)}
                              title="View Validation Errors"
                            >
                              <CircleAlert size={16} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
            <div className="pagination-info">
              {loading ? (
                <>Loading…</>
              ) : (
                <>
                  Showing {showingFrom} to {showingTo} of {totalCount} results
                </>
              )}
            </div>

            <div className="page-buttons">
              <button
                className="page-btn"
                disabled={loading || currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>

              {pageWindow.map((p, idx) =>
                typeof p === "string" ? (
                  <button key={p + idx} className="page-btn ellipsis" disabled>
                    …
                  </button>
                ) : (
                  <button
                    key={p}
                    className={`page-btn ${p === currentPage ? "active" : ""}`}
                    onClick={() => setCurrentPage(p)}
                    disabled={loading}
                  >
                    {p}
                  </button>
                )
              )}

              <button
                className="page-btn"
                disabled={loading || currentPage >= totalPages}
                onClick={() =>
                  setCurrentPage((p) => Math.min(totalPages, p + 1))
                }
              >
                Next
              </button>
            </div>
          </div>

          <ValidationResultsModal
            open={showValidation}
            totalErrors={validationErrors?.length}
            items={validationErrors}
            onClose={() => {
              setShowValidation(false);
              setValidationErrors([]);
              setSelectedLogId(null);
            }}
            onDownloadErrorReport={() =>
              handleDownloadErrorReport(selectedLogId)
            }
            onTryAgain={() => {
              setShowValidation(false);
              setSelectedFile(null);
              navigate("/beatmanagement");
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default BeatMapManagement;
